import React, { useContext, useEffect, useState } from 'react';
import { View, Text, ScrollView, Platform } from 'react-native';
import { NativeBaseProvider } from 'native-base';
import { ActivityIndicator, Button, TextInput } from 'react-native-paper';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { CHECK_CHILD_INVITE } from '../../gql/user/query';
import { ACCEPT_CHILD_INVITE } from '../../gql/user/mutation';
import { AuthContext } from '../../context/AuthContext';
import { styles } from '../Common.style';

// Accepts token either as a direct prop (web deep link) or via route.params (in-app navigation)
function AcceptChildInvite({ token: tokenProp }) {
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const token = tokenProp;

  const [password, setPassword] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);

  const [checkInvite, { loading: checking }] = useLazyQuery(CHECK_CHILD_INVITE, {
    onCompleted: (data) => setInviteInfo(data.checkChildInvite),
    onError: (err) => toast.show(err.message),
  });

  const [acceptInvite, { loading: accepting }] = useMutation(ACCEPT_CHILD_INVITE, {
    onCompleted: (data) => {
      toast.show(data.acceptChildInvite.message);
      // Clear the invite URL and return to normal app flow
      if (Platform.OS === 'web') {
        window.location.href = '/';
      }
    },
    onError: (err) => toast.show(err.message),
  });

  useEffect(() => {
    if (token) checkInvite({ variables: { token } });
  }, [token]);

  if (!token) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>Lien d'invitation invalide.</Text>
      </View>
    );
  }

  if (checking) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!inviteInfo) return null;

  const handleAccept = () => {
    if (!authState.authenticated && !inviteInfo.parentAlreadyExists && !password) {
      toast.show('Veuillez saisir un mot de passe.');
      return;
    }
    acceptInvite({ variables: { token, password: password || undefined } });
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 24 }}>
      <Text style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 16 }}>
        Invitation parent
      </Text>

      <Text style={{ marginBottom: 8 }}>
        Un compte compétiteur a été créé pour{' '}
        <Text style={{ fontWeight: 'bold' }}>
          {inviteInfo.childFirstName} {inviteInfo.childLastName}
        </Text>
        {' '}au Karaté Club Bethoncourt.
      </Text>

      <Text style={{ marginBottom: 24, color: '#555' }}>
        Acceptez l'invitation pour lier ce compte au vôtre en tant que responsable légal.
      </Text>

      {!authState.authenticated && !inviteInfo.parentAlreadyExists && (
        <View style={{ marginBottom: 16 }}>
          <Text style={{ marginBottom: 8, fontWeight: '600' }}>
            Créez votre mot de passe pour finaliser la création de votre compte accompagnateur :
          </Text>
          <TextInput
            label="Mot de passe"
            mode="outlined"
            secureTextEntry
            value={password}
            onChangeText={setPassword}
            left={<TextInput.Icon icon="lock" />}
          />
        </View>
      )}

      {!authState.authenticated && inviteInfo.parentAlreadyExists && (
        <Text style={{ marginBottom: 16, color: '#555' }}>
          Un compte existe déjà pour <Text style={{ fontWeight: 'bold' }}>{inviteInfo.parentEmail}</Text>.{'\n\n'}
          Connectez-vous, puis cliquez à nouveau sur le lien reçu par email pour finaliser la liaison.
        </Text>
      )}

      {(authState.authenticated || !inviteInfo.parentAlreadyExists) && (
        <Button
          mode="contained"
          onPress={handleAccept}
          loading={accepting}
          disabled={accepting}
        >
          Accepter l'invitation
        </Button>
      )}

      {!authState.authenticated && inviteInfo.parentAlreadyExists && (
        <Button
          mode="contained"
          onPress={() => {
            if (Platform.OS === 'web') window.location.href = '/';
          }}
        >
          Se connecter
        </Button>
      )}
    </ScrollView>
  );
}

export default function AcceptChildInviteScreen({ token }) {
  return (
    <NativeBaseProvider>
      <AcceptChildInvite token={token} />
    </NativeBaseProvider>
  );
}
